<script lang="ts" setup>
const currentYear = new Date().getFullYear()

// Plan du site : liens internes vers les pages de prestations, de régions et de formations.
const linkGroups = [
  {
    title: 'Prestations',
    links: [
      { to: '/prestations', label: 'Toutes nos prestations' },
      { to: '/prestations/dispositif-secours-evenement-sportif', label: 'Événements sportifs' },
      { to: '/prestations/poste-de-secours-festival-concert', label: 'Festivals et concerts' },
      { to: '/prestations/dispositif-secours-seminaire-salon-entreprise', label: 'Séminaires et salons' },
      { to: '/prestations/poste-de-secours-commune-brocante', label: 'Manifestations communales' },
      { to: '/societe-preventif-medical-belgique', label: 'Dispositifs médicaux préventifs' },
    ],
  },
  {
    title: 'Postes de secours par région',
    links: [
      { to: '/prestations/poste-de-secours-hainaut', label: 'Poste de secours Hainaut' },
      { to: '/prestations/poste-de-secours-liege', label: 'Poste de secours Liège' },
      { to: '/prestations/poste-de-secours-luxembourg', label: 'Poste de secours Luxembourg' },
      { to: '/prestations/poste-de-secours-namur', label: 'Poste de secours Namur' },
      { to: '/prestations/poste-de-secours-bruxelles', label: 'Poste de secours Bruxelles' },
    ],
  },
  {
    title: 'Formations',
    links: [
      { to: '/formations', label: 'Toutes nos formations' },
      { to: '/formations/formations-grand-public', label: 'Formations grand public' },
      { to: '/formations/stages-secouristes-juniors', label: 'Stages Secouristes Juniors' },
      { to: '/formations/secouriste-entreprise', label: 'Secouriste en entreprise' },
      { to: '/formations/recyclage', label: 'Recyclage secouriste' },
      { to: '/formations/atnup-virton', label: 'Formation ATNUP' },
    ],
  },
  {
    title: 'À propos',
    links: [
      { to: '/qui-sommes-nous', label: 'Qui sommes-nous' },
      { to: '/guide-defibrillateur-dae-entreprise-belgique', label: 'Défibrillateur en entreprise' },
      { to: '/recrutement-volontaires', label: 'Recrutement volontaires' },
    ],
  },
]

const trackNavigationCta = (cta: string) => {
  if (import.meta.client) {
    useTrackEvent('CTA: Navigation', {
      props: {
        cta,
      },
    })
  }
}
</script>

<template>
  <footer class="public-content mt-20">
    <div class="mx-auto max-w-7xl px-4 pb-12 sm:px-6 lg:px-8">
      <div class="public-section mb-8 px-6 py-8 md:px-10">
        <div class="flex flex-col items-start justify-between gap-6 md:flex-row md:items-center">
          <div>
            <p class="mb-2 text-sm font-semibold text-brand-blue-700 dark:text-brand-blue-300">
              Assistance événementielle
            </p>
            <h3 class="public-h3 text-2xl">
              Un besoin terrain imminent ?
            </h3>
            <p class="public-muted mt-2 text-sm md:text-base">
              Nous déployons des équipes secouristes, des postes de soins et des moyens mobiles sur mesure.
            </p>
          </div>
          <div class="flex w-full flex-col gap-3 sm:w-auto sm:flex-row">
            <UButton
              to="/devis"
              label="Demander un devis"
              size="lg"
              @click="trackNavigationCta('devis_footer')"
            />
            <UButton
              variant="outline"
              to="/contact"
              label="Nous contacter"
              size="lg"
            />
          </div>
        </div>
      </div>

      <div class="rounded-[var(--radius-section)] border border-[var(--line-soft)] bg-[var(--surface-1)] px-5 py-8 sm:px-8">
        <nav
          class="mb-8 grid grid-cols-2 gap-x-6 gap-y-8 border-b border-[var(--line-soft)] pb-8 md:grid-cols-4"
          aria-label="Plan du site"
        >
          <div
            v-for="group in linkGroups"
            :key="group.title"
          >
            <p class="text-sm font-semibold text-[var(--ink-900)]">
              {{ group.title }}
            </p>
            <ul class="mt-3 space-y-2">
              <li
                v-for="link in group.links"
                :key="link.to"
              >
                <NuxtLink
                  :to="link.to"
                  class="text-sm text-[var(--ink-700)] transition hover:text-brand-blue-700 hover:underline dark:hover:text-brand-blue-300"
                >
                  {{ link.label }}
                </NuxtLink>
              </li>
            </ul>
          </div>
        </nav>

        <nav
          class="flex flex-wrap justify-center -mx-5 -my-2"
          aria-label="Footer"
        >
          <div class="px-5 py-2">
            <UButton
              variant="link"
              to="/mentions-legales"
              label="Mentions légales"
              class="font-normal text-[var(--ink-700)]"
            />
          </div>

          <div class="px-5 py-2">
            <UButton
              variant="link"
              to="/politique-confidentialite"
              label="Politique de confidentialité"
              class="font-normal text-[var(--ink-700)]"
            />
          </div>

          <div class="px-5 py-2">
            <UButton
              variant="link"
              to="/postuler"
              label="Postuler"
              class="font-normal text-[var(--ink-700)]"
              @click="trackNavigationCta('postuler_footer')"
            />
          </div>

          <div class="px-5 py-2">
            <UButton
              variant="link"
              to="/faq"
              label="FAQ"
              class="font-normal text-[var(--ink-700)]"
            />
          </div>

          <div class="px-5 py-2">
            <UButton
              variant="link"
              to="/partenaires"
              label="Partenaires"
              class="font-normal text-[var(--ink-700)]"
            />
          </div>

          <div class="px-5 py-2">
            <UButton
              variant="link"
              to="/presse"
              label="Presse"
              class="font-normal text-[var(--ink-700)]"
            />
          </div>

          <div class="px-5 py-2">
            <UButton
              variant="link"
              to="/lexique"
              label="Lexique"
              class="font-normal text-[var(--ink-700)]"
            />
          </div>
        </nav>
        <div class="flex justify-center mt-8 space-x-6">
          <span class="inline-flex justify-center w-full gap-3 m-auto md:justify-start md:w-auto">
            <UButton
              icon="i-ic-baseline-facebook"
              size="lg"
              variant="link"
              to="https://www.facebook.com/AmbulancePresent/"
              target="_blank"
              aria-label="Notre page Facebook"
            />

            <UButton
              icon="i-mdi-instagram"
              size="lg"
              variant="link"
              to="https://www.instagram.com/ambulancepresent/"
              target="_blank"
              aria-label="Notre page Instagram"
            />

            <UButton
              icon="i-ic-baseline-tiktok"
              size="lg"
              variant="link"
              to="https://www.tiktok.com/@ambulancepresent"
              target="_blank"
              aria-label="Notre page TikTok"
            />
          </span>
        </div>
        <p class="mt-8 text-center">
          <span class="mx-auto mt-2 text-sm text-[var(--ink-700)]">
            Copyright © 2017 - {{ currentYear }}
            <NuxtLink
              to="/"
              class="w-6 h-6 transition fill-black hover:text-brand-blue-600 cursor-pointer"
              rel="noopener noreferrer"
            >
              Ambulance Présent 2.0 ASBL
            </NuxtLink>
          </span>
        </p>
        <p class="mt-2 text-center text-xs text-[var(--ink-700)]">
          <NuxtLink
            to="https://www.bugsnag.com/"
            target="_blank"
            rel="noopener noreferrer nofollow"
            class="transition hover:text-[var(--ink-900)]"
          >
            Propulsé par Bugsnag
          </NuxtLink>
        </p>
      </div>
    </div>
  </footer>
</template>
